import api from '@/services/api/mobileApiService';
import { logger } from '@/utils/logger';

/**
 * Mirrors the server's "status" column. `null` means "not yet fetched";
 * 'none' means "fetched but user has no row" (shouldn't happen post-backfill,
 * but handled gracefully).
 */
export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'incomplete'
  | 'none'
  | null;

export interface SubscriptionPayload {
  status: SubscriptionStatus;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  planSlug: string | null;
  planName: string | null;
  monthlyPriceCents: number | null;
  currency: string | null;
  daysLeftInTrial: number | null;
  /**
   * True when the user has made the post-signup plan choice
   * (individuale confirmed OR team checkout started). Phase 2 uses this
   * to gate the mandatory onboarding wizard.
   */
  onboardingCompleted: boolean;
  /**
   * Whether the subscription is tied to a user (individual plan) or an
   * organization (team plan). Null only when the user has no
   * subscription at all (shouldn't happen post-backfill).
   */
  ownerType: 'user' | 'org' | null;
  /**
   * For org-scoped subscriptions only: number of seats purchased
   * (mirrors organizations.seats_purchased, which is also the Stripe
   * subscription quantity). null for individual subscriptions.
   */
  seats: number | null;
  /**
   * For org-scoped subscriptions only: the organization's display name.
   */
  orgName: string | null;
}

export interface SubscriptionPlan {
  slug: string;
  displayName: string;
  monthlyPriceCents: number;
  currency: string;
  trialDays: number;
  seatBased: boolean;
  features: Record<string, unknown>;
}

export interface SubscriptionState {
  subscription: SubscriptionPayload;
  plans: SubscriptionPlan[];
  // Reason the paywall is being shown, set by the 402 interceptor.
  paywallReason: 'trial_expired' | 'subscription_inactive' | 'no_subscription' | null;
  // Checkout lifecycle on the frontend. Used by CheckoutReturnHandler to show
  // the "Attivazione in corso…" interstitial while polling after Stripe return.
  checkoutPolling: boolean;
  checkoutPollTimedOut: boolean;
  // Stripe mode surfaced by GET /api/billing/me. `testMode=true` means the
  // backend is running against sandbox keys and the frontend should show a
  // visible indicator so no-one confuses a test charge for a real one.
  stripeMode: 'test' | 'live' | null;
  testMode: boolean;
}

const emptySubscription: SubscriptionPayload = {
  status: null,
  trialEndsAt: null,
  currentPeriodEnd: null,
  planSlug: null,
  planName: null,
  monthlyPriceCents: null,
  currency: null,
  daysLeftInTrial: null,
  onboardingCompleted: false,
  ownerType: null,
  seats: null,
  orgName: null,
};

export const subscriptionSlice: SubscriptionState = {
  subscription: emptySubscription,
  plans: [],
  paywallReason: null,
  checkoutPolling: false,
  checkoutPollTimedOut: false,
  stripeMode: null,
  testMode: false,
};

// ─── Server response shapes ─────────────────────────────────────────────────
//
// Unlike the org routes, the billing routes return their fields at the TOP
// level of the response body (no `{ data }` envelope). mobileApiService returns
// that body directly, so the handlers read response.subscription / .plans / .url
// with no extra `.data` hop. Typing those bodies here removes the `any` casts.

interface BillingMeResponse {
  success?: boolean;
  subscription?: SubscriptionPayload;
  testMode?: boolean;
  stripeMode?: 'test' | 'live' | string;
}

interface PlansResponse {
  success?: boolean;
  plans?: SubscriptionPlan[];
}

interface CheckoutResponse {
  success?: boolean;
  url?: string;
}

interface CompleteIndividualResponse {
  success?: boolean;
  subscription?: SubscriptionPayload;
}

// NOTE: the `set`/`get` parameter types below intentionally mirror the loose
// store-composition signature used across every slice in this codebase. The
// strongly-typed action surface lives in src/types/store.ts. Do not narrow
// these — it would diverge from the other slices and break store composition.
export type SubscriptionActions = (
  set: (partial: ((state: any) => void) | Record<string, any>) => void,
  get: () => any
) => {
  setSubscription: (payload: SubscriptionPayload) => void;
  setPaywallReason: (reason: SubscriptionState['paywallReason']) => void;
  fetchSubscription: () => Promise<SubscriptionPayload | null>;
  fetchPlans: () => Promise<void>;
  startCheckout: () => Promise<void>;
  clearSubscription: () => void;
  /** Helpful derived getters. */
  hasAccess: () => boolean;
  isTrialing: () => boolean;
  beginCheckoutPolling: () => void;
  endCheckoutPolling: (timedOut: boolean) => void;
  /**
   * Phase 2: mark the individual path of the onboarding wizard complete.
   * No Stripe call. The existing trial keeps running until trial_ends_at.
   */
  completeIndividualOnboarding: () => Promise<SubscriptionPayload | null>;
  /**
   * Phase 2: start the company Stripe Checkout. Does a window.location
   * redirect on success — anything after this point only runs if the
   * request failed.
   */
  startCompanyCheckout: (args: {
    orgName: string;
    seats: number;
    inviteEmails: string[];
  }) => Promise<void>;
};

export const subscriptionActions: SubscriptionActions = (set, get) => ({
  setSubscription: (payload) => {
    set({ subscription: payload });
  },

  setPaywallReason: (reason) => {
    set({ paywallReason: reason });
  },

  /**
   * Fetch the current subscription from /api/billing/me. Safe to call at
   * any time (it only reads from the DB, never hits Stripe).
   *
   * Returns the payload so polling loops can branch on the value without
   * re-reading the store.
   */
  fetchSubscription: async () => {
    try {
      // mobileApiService.get() already returns the response body (axios
      // response.data). The server shape is { success, subscription,
      // testMode, stripeMode }, so we access response.subscription
      // directly (no extra .data hop).
      const response = await api.get<BillingMeResponse>('/api/billing/me');
      const payload = response?.subscription;
      // Stripe mode surfaced alongside the subscription. Safe defaults
      // (testMode=true) so a malformed response doesn't make a live
      // environment silently look like prod without the banner.
      const mode: 'test' | 'live' = response?.stripeMode === 'live' ? 'live' : 'test';
      const testMode = response?.testMode === true || mode === 'test';
      if (payload) {
        set({ subscription: payload, stripeMode: mode, testMode });
        return payload;
      }
      set({ subscription: emptySubscription, stripeMode: mode, testMode });
      return null;
    } catch (err: unknown) {
      // A 402 here would be ironic — /api/billing/me is intentionally
      // NOT behind requireActiveSubscription. Any failure is transient.
      logger.warn('fetchSubscription failed', { error: errorMessage(err) });
      return null;
    }
  },

  fetchPlans: async () => {
    try {
      const response = await api.get<PlansResponse>('/api/billing/plans');
      const plans = response?.plans;
      if (plans) set({ plans });
    } catch (err: unknown) {
      logger.warn('fetchPlans failed', { error: errorMessage(err) });
    }
  },

  /**
   * Kick off the Stripe Checkout redirect. The backend creates a Checkout
   * Session and returns its URL; we navigate the whole window there.
   *
   * On return, Stripe redirects to <app>/?checkout=success (or
   * ?checkout=cancel). CheckoutReturnHandler in App.tsx picks up that
   * query string and polls /me. Activation happens strictly via webhook;
   * this function never writes status='active' locally.
   */
  startCheckout: async () => {
    try {
      // api.post(url, data) — passing undefined for body is fine, the
      // endpoint ignores body. Server returns { success, url }, de-wrapped.
      const response = await api.post<CheckoutResponse>('/api/billing/checkout-session', undefined);
      const url = response?.url;
      if (!url) {
        logger.error('startCheckout: no url returned', { response });
        return;
      }
      // Full-page redirect — Stripe Checkout runs on their origin.
      window.location.href = url;
    } catch (err: unknown) {
      logger.error('startCheckout failed', { error: errorMessage(err), status: httpStatus(err) });
      throw err;
    }
  },

  clearSubscription: () => {
    set({
      subscription: emptySubscription,
      plans: [],
      paywallReason: null,
      checkoutPolling: false,
      checkoutPollTimedOut: false,
    });
  },

  hasAccess: () => {
    const s = get().subscription as SubscriptionPayload;
    if (!s || !s.status) return false;
    if (s.status === 'active') return true;
    if (s.status === 'trialing') {
      if (!s.trialEndsAt) return true;
      // A malformed (non-ISO) date parses to NaN; NaN > now is false, which
      // would wrongly deny access on a trialing account. Grant access in that
      // case rather than locking the user out on bad server data.
      const ends = new Date(s.trialEndsAt).getTime();
      return Number.isNaN(ends) ? true : ends > Date.now();
    }
    return false;
  },

  isTrialing: () => {
    const s = get().subscription as SubscriptionPayload;
    return s?.status === 'trialing' && !!s.trialEndsAt && new Date(s.trialEndsAt).getTime() > Date.now();
  },

  beginCheckoutPolling: () => {
    set({ checkoutPolling: true, checkoutPollTimedOut: false });
  },

  endCheckoutPolling: (timedOut: boolean) => {
    set({ checkoutPolling: false, checkoutPollTimedOut: timedOut });
  },

  completeIndividualOnboarding: async () => {
    try {
      const response = await api.post<CompleteIndividualResponse>('/api/billing/complete-individual', undefined);
      const payload = response?.subscription;
      if (payload) {
        set({ subscription: payload });
        return payload;
      }
      return null;
    } catch (err: unknown) {
      logger.error('completeIndividualOnboarding failed', { error: errorMessage(err) });
      throw err;
    }
  },

  startCompanyCheckout: async ({ orgName, seats, inviteEmails }) => {
    try {
      const response = await api.post<CheckoutResponse>('/api/billing/checkout-session-company', {
        orgName,
        seats,
        inviteEmails,
      });
      const url = response?.url;
      if (!url) {
        logger.error('startCompanyCheckout: no url returned', { response });
        throw new Error('Checkout URL mancante');
      }
      window.location.href = url;
    } catch (err: unknown) {
      logger.error('startCompanyCheckout failed', { error: errorMessage(err), status: httpStatus(err) });
      throw err;
    }
  },
});

// ─── Internal helpers ───────────────────────────────────────────────────────

/**
 * Extract a short, log-safe message from an unknown thrown value. Catch
 * clauses are typed `unknown` under strict mode; this narrows once so the
 * action bodies stay readable.
 */
function errorMessage(error: unknown): string | undefined {
  if (error && typeof error === 'object' && 'message' in error) {
    const msg = (error as { message?: unknown }).message;
    if (typeof msg === 'string') return msg;
  }
  return undefined;
}

/**
 * Pull a numeric `status` off a thrown value. The original code read
 * `err?.status` (the formatError-normalized shape from mobileApiService),
 * so we check the top-level `status` field first, then axios `response.status`.
 */
function httpStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object') {
    const top = (error as { status?: unknown }).status;
    if (typeof top === 'number') return top;
    const response = (error as { response?: unknown }).response;
    if (response && typeof response === 'object' && 'status' in response) {
      const status = (response as { status?: unknown }).status;
      if (typeof status === 'number') return status;
    }
  }
  return undefined;
}
